import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, UserPlus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { companion } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { Input } from "@/components/ui/input";

const CompanionCrew = () => {
  const { user, ready } = useCompanionAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "crew" });

  const load = async () => {
    setLoading(true);
    try { setItems((await companion.get("/crew")).data); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  if (ready && user?.role !== "admin") return <Navigate to="/companion" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await companion.post("/crew", form);
      toast.success(`Account created for ${form.name}.`);
      setForm({ name: "", email: "", phone: "", password: "", role: "crew" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create user.");
    } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await companion.patch(`/crew/${u.id}`, { is_active: !u.is_active });
      load();
    } catch { toast.error("Couldn't update."); }
  };

  const remove = async (u) => {
    if (u.id === user.id) { toast.error("You can't delete your own account."); return; }
    if (!window.confirm(`Disable ${u.name || u.email}? They'll no longer be able to sign in.`)) return;
    try {
      await companion.delete(`/crew/${u.id}`);
      load();
    } catch { toast.error("Couldn't disable user."); }
  };

  return (
    <div className="px-4 py-5 space-y-4" data-testid="companion-crew-page">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Admin</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">Crew accounts</h1>
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-1.5 rounded-md bg-crest-green text-white px-4 py-2 text-sm font-semibold"
          data-testid="crew-add-toggle">
          <UserPlus className="h-4 w-4" /> {showForm ? "Cancel" : "Add"}
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="space-y-3 rounded-lg bg-white border border-border p-5" data-testid="crew-add-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Name *</label>
              <Input value={form.name} onChange={set("name")} required className="mt-2 h-11" data-testid="crew-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Email *</label>
              <Input type="email" value={form.email} onChange={set("email")} required className="mt-2 h-11" data-testid="crew-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Phone</label>
              <Input value={form.phone} onChange={set("phone")} className="mt-2 h-11" data-testid="crew-phone" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Password (min 8) *</label>
              <Input type="text" minLength={8} value={form.password} onChange={set("password")} required className="mt-2 h-11" data-testid="crew-password" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Role</label>
              <select value={form.role} onChange={set("role")} className="mt-2 h-11 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="crew-role">
                <option value="crew">Crew</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy} className="w-full h-11 rounded-md bg-crest-green text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="crew-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
          </button>
        </form>
      )}

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-crest-green" /></div>
      ) : (
        <ul className="space-y-2">
          {items.map((u) => (
            <li key={u.id} className={`rounded-lg border bg-white p-4 flex items-start gap-3 ${u.is_active === false ? "opacity-60" : ""}`}
                data-testid={`crew-row-${u.id}`}>
              <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green text-white font-display font-bold shrink-0">
                {(u.name || u.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-crest-navy truncate">{u.name || u.email}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                <div className="mt-1 inline-flex items-center gap-1.5 text-[11px]">
                  <span className={`px-1.5 py-0.5 rounded ${u.role === "admin" ? "bg-crest-gold/20 text-amber-800" : "bg-crest-green-100 text-crest-green"}`}>{u.role}</span>
                  {u.is_active === false && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">disabled</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggleActive(u)} aria-label="Toggle active" className="h-9 w-9 grid place-items-center rounded text-crest-navy/70 hover:text-crest-navy" data-testid={`crew-toggle-${u.id}`}>
                  <Power className="h-4 w-4" />
                </button>
                <button onClick={() => remove(u)} aria-label="Disable" className="h-9 w-9 grid place-items-center rounded text-destructive" data-testid={`crew-remove-${u.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CompanionCrew;
